import { bollinger, donchian, ema, macd, rsi, sma, stochastic, supertrend } from "./indicators";
import { calculateKaufmanEfficiencyRatio } from "../Chart/indicatorCalculations";
import { detectHMMRegimesCached, HMM_LOOKBACK_OPTIONS, HMM_MODEL_OPTIONS, HMM_TRAINING_SPLITS } from "../Chart/hmmRegimes";

/** Each strategy turns candles into a DESIRED position per bar close: 1 long, -1 short, 0 flat.
 *  The engine trades that position at the NEXT bar's open (no look-ahead). */

const col = (candles, key) => candles.map((c) => c[key]);
const ok = (...v) => v.every((x) => Number.isFinite(x));

/** Hold a state: set to 1/-1 on an entry signal, 0 on an exit signal, otherwise keep. */
function stateMachine(n, step) {
  const out = new Array(n).fill(0);
  let pos = 0;
  for (let i = 0; i < n; i += 1) {
    pos = step(i, pos);
    out[i] = pos;
  }
  return out;
}

export function buildHMMBollingerPositions(candles, p, hmm) {
  const closes = col(candles, "close");
  const period = Math.min(200, Math.max(2, Math.floor(Number(p.bbPeriod) || 20)));
  const deviation = Math.min(5, Math.max(0.5, Number(p.bbMult) || 2));
  const modelMode = p.hmmModelMode === "causal200" ? "causal200" : "holdout";
  const modelStatus = modelMode === "causal200" ? (hmm.causalStatus || hmm.status) : hmm.status;
  const modelReason = modelMode === "causal200" ? (hmm.causalReason || hmm.reason) : hmm.reason;
  if (modelStatus !== "ready") throw new Error(modelReason || "HMM regime is not available for these candles.");
  const tradeBars = modelMode === "causal200"
    ? (hmm.causalBars || [])
    : (hmm.bars || []).filter((bar) => bar.phase === "holdout");
  if (!tradeBars.length) {
    throw new Error(modelMode === "causal200"
      ? (modelReason || "The HMM needs 200 calibration feature bars before Causal mode can trade.")
      : "The HMM needs a usable forward-filtered holdout before it can take trades.");
  }
  const states = new Map(tradeBars.map((bar) => [Number(bar.time), bar.kind]));
  const bands = bollinger(closes, period, deviation);
  const touchByWick = p.touchMode !== "close";
  const exitMode = ["either", "oppositeBand", "regimeChange"].includes(p.exitMode) ? p.exitMode : "either";
  const exitOnRegimeChange = exitMode === "either" || exitMode === "regimeChange";
  const exitOnOppositeBand = exitMode === "either" || exitMode === "oppositeBand";
  const maxTrades = Math.min(100, Math.max(1, Math.floor(Number(p.maxTrades) || 1)));
  const selectedRegime = ["range", "trend", "highVol"].includes(p.hmmRegime) ? p.hmmRegime : "range";
  const out = new Array(candles.length).fill(0);
  const entryReasons = new Array(candles.length).fill(null);
  const exitReasons = new Array(candles.length).fill(null);
  let side = 0;
  let count = 0;

  for (let i = 0; i < candles.length; i += 1) {
    if (!ok(bands.upper[i], bands.mid[i], bands.lower[i])) {
      out[i] = side * count;
      continue;
    }
    const activeRegime = states.get(Number(candles[i].time));
    if (!activeRegime) {
      if (side !== 0 && exitOnRegimeChange) {
        exitReasons[i] = modelMode === "causal200" ? "causalWarmup" : "holdoutEnd";
        side = 0;
        count = 0;
      }
      out[i] = side * count;
      continue;
    }
    const inSelectedRegime = activeRegime === selectedRegime;
    const lowerReached = touchByWick ? candles[i].low <= bands.lower[i] : closes[i] <= bands.lower[i];
    const upperReached = touchByWick ? candles[i].high >= bands.upper[i] : closes[i] >= bands.upper[i];
    const previousLowerReached = i > 0 && Number.isFinite(bands.lower[i - 1])
      && (touchByWick ? candles[i - 1].low <= bands.lower[i - 1] : closes[i - 1] <= bands.lower[i - 1]);
    const previousUpperReached = i > 0 && Number.isFinite(bands.upper[i - 1])
      && (touchByWick ? candles[i - 1].high >= bands.upper[i - 1] : closes[i - 1] >= bands.upper[i - 1]);
    const lowerTouch = lowerReached && !previousLowerReached;
    const upperTouch = upperReached && !previousUpperReached;
    const spansBothBands = lowerTouch && upperTouch;

    if (side !== 0 && exitOnRegimeChange && !inSelectedRegime) {
      exitReasons[i] = "regimeChange";
      side = 0;
      count = 0;
    } else if (side === 1 && exitOnOppositeBand && upperReached) {
      exitReasons[i] = "oppositeBand";
      side = 0;
      count = 0;
    } else if (side === -1 && exitOnOppositeBand && lowerReached) {
      exitReasons[i] = "oppositeBand";
      side = 0;
      count = 0;
    } else if (inSelectedRegime && !spansBothBands && side === 0 && lowerTouch) {
      side = 1;
      count = 1;
      entryReasons[i] = "lowerBand";
    } else if (inSelectedRegime && !spansBothBands && side === 0 && upperTouch) {
      side = -1;
      count = 1;
      entryReasons[i] = "upperBand";
    } else if (inSelectedRegime && !spansBothBands && side === 1 && lowerTouch) {
      count = Math.min(maxTrades, count + 1);
    } else if (inSelectedRegime && !spansBothBands && side === -1 && upperTouch) {
      count = Math.min(maxTrades, count + 1);
    }
    out[i] = side * count;
  }
  out.hmmSignals = { entryReasons, exitReasons, modelMode };
  return out;
}

export const STRATEGIES = [
  {
    id: "sma_cross",
    name: "SMA crossover",
    blurb: "Long when the fast average is above the slow one, short when below.",
    params: [
      { key: "fast", label: "Fast", value: 20, min: 2, max: 400 },
      { key: "slow", label: "Slow", value: 50, min: 3, max: 800 },
    ],
    positions(candles, p) {
      const c = col(candles, "close");
      const f = sma(c, p.fast);
      const s = sma(c, p.slow);
      return c.map((_, i) => (ok(f[i], s[i]) ? (f[i] > s[i] ? 1 : -1) : 0));
    },
  },
  {
    id: "ema_cross",
    name: "EMA crossover",
    blurb: "Same idea with exponential averages (reacts faster).",
    params: [
      { key: "fast", label: "Fast", value: 12, min: 2, max: 400 },
      { key: "slow", label: "Slow", value: 26, min: 3, max: 800 },
    ],
    positions(candles, p) {
      const c = col(candles, "close");
      const f = ema(c, p.fast);
      const s = ema(c, p.slow);
      return c.map((_, i) => (ok(f[i], s[i]) ? (f[i] > s[i] ? 1 : -1) : 0));
    },
  },
  {
    id: "rsi_reversion",
    name: "RSI mean reversion",
    blurb: "Buy when RSI drops below the low level, sell when it rises above the high level, exit at 50.",
    params: [
      { key: "period", label: "RSI period", value: 14, min: 2, max: 100 },
      { key: "low", label: "Buy below", value: 30, min: 1, max: 49 },
      { key: "high", label: "Sell above", value: 70, min: 51, max: 99 },
    ],
    positions(candles, p) {
      const r = rsi(col(candles, "close"), p.period);
      return stateMachine(candles.length, (i, pos) => {
        if (!ok(r[i])) return pos;
        if (r[i] < p.low) return 1;
        if (r[i] > p.high) return -1;
        if (pos === 1 && r[i] >= 50) return 0;
        if (pos === -1 && r[i] <= 50) return 0;
        return pos;
      });
    },
  },
  {
    id: "bollinger_reversion",
    name: "Bollinger reversion",
    blurb: "Buy a close below the lower band, sell above the upper band, exit at the middle line.",
    params: [
      { key: "period", label: "Period", value: 20, min: 2, max: 200 },
      { key: "mult", label: "Std devs", value: 2, min: 0.5, max: 5, step: 0.1 },
    ],
    positions(candles, p) {
      const c = col(candles, "close");
      const b = bollinger(c, p.period, p.mult);
      return stateMachine(c.length, (i, pos) => {
        if (!ok(b.mid[i])) return pos;
        if (c[i] < b.lower[i]) return 1;
        if (c[i] > b.upper[i]) return -1;
        if (pos === 1 && c[i] >= b.mid[i]) return 0;
        if (pos === -1 && c[i] <= b.mid[i]) return 0;
        return pos;
      });
    },
  },
  {
    id: "guppy_bollinger_touch",
    name: "Guppy + Bollinger touch",
    adminOnly: true,
    blurb: "Long when bullish Guppy alignment touches the lower band; short when bearish alignment touches the upper band. Exit at the opposite band. Wick or close touches are selectable; signals execute at the next bar open. The six-line Guppy groups use standard offsets from each base.",
    params: [
      { key: "shortBase", label: "Guppy short EMA base", value: 3, min: 2, max: 100 },
      { key: "longBase", label: "Guppy long EMA base", value: 30, min: 10, max: 400 },
      {
        key: "alignment",
        label: "Guppy positive",
        type: "select",
        value: "average",
        options: [
          { value: "average", label: "Short group average above/below long" },
          { value: "strict", label: "Full ribbon alignment" },
        ],
      },
      { key: "bbPeriod", label: "Bollinger period", value: 20, min: 2, max: 200 },
      { key: "bbMult", label: "Bollinger deviation", value: 2, min: 0.5, max: 5, step: 0.1 },
      {
        key: "touchMode",
        label: "Band touch",
        type: "select",
        value: "wick",
        options: [
          { value: "wick", label: "Wick touches band (high/low)" },
          { value: "close", label: "Candle closes beyond band" },
        ],
      },
    ],
    positions(candles, p) {
      const closes = col(candles, "close");
      const period = Math.min(200, Math.max(2, Math.floor(Number(p.bbPeriod) || 20)));
      const deviation = Math.min(5, Math.max(0.5, Number(p.bbMult) || 2));
      const shortBase = Math.min(100, Math.max(2, Math.floor(Number(p.shortBase) || 3)));
      const longBase = Math.min(400, Math.max(10, Math.floor(Number(p.longBase) || 30)));
      const shortOffsets = [0, 2, 5, 7, 9, 12];
      const longOffsets = [0, 5, 10, 15, 20, 30];
      const shortLines = shortOffsets.map((offset) => ema(closes, shortBase + offset));
      const longLines = longOffsets.map((offset) => ema(closes, longBase + offset));
      const bands = bollinger(closes, period, deviation);
      const touchByWick = p.touchMode !== "close";
      const strictAlignment = p.alignment === "strict";

      return stateMachine(candles.length, (i, pos) => {
        const shortValues = shortLines.map((line) => line[i]);
        const longValues = longLines.map((line) => line[i]);
        if (!ok(bands.upper[i], bands.lower[i], ...shortValues, ...longValues)) return pos;

        const shortAverage = shortValues.reduce((sum, value) => sum + value, 0) / shortValues.length;
        const longAverage = longValues.reduce((sum, value) => sum + value, 0) / longValues.length;
        const bullish = strictAlignment
          ? Math.min(...shortValues) > Math.max(...longValues)
          : shortAverage > longAverage;
        const bearish = strictAlignment
          ? Math.max(...shortValues) < Math.min(...longValues)
          : shortAverage < longAverage;
        const lowerTouch = touchByWick ? candles[i].low <= bands.lower[i] : closes[i] <= bands.lower[i];
        const upperTouch = touchByWick ? candles[i].high >= bands.upper[i] : closes[i] >= bands.upper[i];

        if (pos === 1 && upperTouch) return 0;
        if (pos === -1 && lowerTouch) return 0;
        if (pos !== 0) return pos;
        if (bullish && lowerTouch) return 1;
        if (bearish && upperTouch) return -1;
        return pos;
      });
    },
  },
  {
    id: "hmm_bollinger_touch",
    name: "HMM + Bollinger touch",
    adminOnly: true,
    blurb: "Choose the HMM model, training/holdout split and feature lookback. Holdout mode trades only its forward-filtered final segment; Causal 200 mode starts after 200 calibrated feature bars and filters forward without future data. Enter on an outer Bollinger touch in the selected regime (Range by default); exit on the opposite band OR a regime change. Signal markers show the touch candle; entry and exit fills use the next bar open.",
    params: [
      { key: "bbPeriod", label: "Bollinger period", value: 20, min: 2, max: 200 },
      { key: "bbMult", label: "Bollinger deviation", value: 2, min: 0.5, max: 5, step: 0.1 },
      { key: "hmmLookback", label: "Feature lookback", type: "select", value: "20", options: HMM_LOOKBACK_OPTIONS },
      { key: "hmmModelMode", label: "HMM model mode", type: "select", value: "holdout", options: HMM_MODEL_OPTIONS },
      { key: "hmmTrainPct", label: "HMM training / holdout split", type: "select", value: "70", options: HMM_TRAINING_SPLITS },
      {
        key: "hmmRegime",
        label: "Trade only in HMM regime",
        type: "select",
        value: "range",
        options: [
          { value: "range", label: "Range" },
          { value: "trend", label: "Trend" },
          { value: "highVol", label: "High volatility" },
        ],
      },
      {
        key: "touchMode",
        label: "Band touch",
        type: "select",
        value: "wick",
        options: [
          { value: "wick", label: "Wick touches band (high/low)" },
          { value: "close", label: "Candle closes beyond band" },
        ],
      },
      {
        key: "exitMode",
        label: "Exit when",
        type: "select",
        value: "either",
        options: [
          { value: "either", label: "Opposite Bollinger band OR HMM regime changes" },
          { value: "oppositeBand", label: "Opposite Bollinger band is touched" },
          { value: "regimeChange", label: "HMM changes away from selected regime" },
        ],
      },
      { key: "maxTrades", label: "Max trades at a time", value: 1, min: 1, max: 100, step: 1 },
    ],
    positions(candles, p) {
      const lookback = Math.min(500, Math.max(2, Math.floor(Number(p.hmmLookback) || 20)));
      const trainingFraction = Number(p.hmmTrainPct) / 100 || 0.7;
      const hmm = detectHMMRegimesCached(candles, { lookback, trainingFraction });
      return buildHMMBollingerPositions(candles, p, hmm);
    },
  },
  {
    id: "range_envelope_touch",
    name: "Range + moving average envelope",
    adminOnly: true,
    blurb: "Fade the envelope edges only while Kaufman efficiency is below the range threshold. Buy a lower-envelope touch, sell an upper-envelope touch, and exit at the opposite edge. Repeated fresh touches can add entries up to the max-trades setting; fills execute at the next bar open.",
    params: [
      { key: "rangePeriod", label: "Range period", value: 20, min: 2, max: 200 },
      { key: "rangeSmooth", label: "Range smoothing", value: 5, min: 1, max: 100 },
      { key: "rangeThreshold", label: "Range threshold (<)", value: 0.25, min: 0.01, max: 0.95, step: 0.01 },
      { key: "envelopePeriod", label: "Envelope period", value: 20, min: 2, max: 200 },
      { key: "envelopePct", label: "Envelope width (%)", value: 0.3, min: 0.01, max: 10, step: 0.01 },
      { key: "maxTrades", label: "Max trades at a time", value: 1, min: 1, max: 100, step: 1 },
      {
        key: "touchMode",
        label: "Envelope touch",
        type: "select",
        value: "wick",
        options: [
          { value: "wick", label: "Wick touches band (high/low)" },
          { value: "close", label: "Candle closes beyond band" },
        ],
      },
    ],
    positions(candles, p) {
      const closes = col(candles, "close");
      const rangePeriod = Math.min(200, Math.max(2, Math.floor(Number(p.rangePeriod) || 20)));
      const rangeSmooth = Math.min(100, Math.max(1, Math.floor(Number(p.rangeSmooth) || 5)));
      const rangeThreshold = Math.min(0.95, Math.max(0.01, Number(p.rangeThreshold) || 0.25));
      const envelopePeriod = Math.min(200, Math.max(2, Math.floor(Number(p.envelopePeriod) || 20)));
      const envelopePct = Math.min(10, Math.max(0.01, Number(p.envelopePct) || 0.3));
      const maxTrades = Math.min(100, Math.max(1, Math.floor(Number(p.maxTrades) || 1)));
      const efficiency = new Array(candles.length).fill(NaN);
      calculateKaufmanEfficiencyRatio(candles, rangePeriod, rangeSmooth).forEach((point, index) => {
        efficiency[index + rangePeriod] = point.value;
      });
      const middle = sma(closes, envelopePeriod);
      const upper = middle.map((value) => value * (1 + envelopePct / 100));
      const lower = middle.map((value) => value * (1 - envelopePct / 100));
      const touchByWick = p.touchMode !== "close";
      const out = new Array(candles.length).fill(0);
      let side = 0;
      let count = 0;

      for (let i = 0; i < candles.length; i += 1) {
        if (!ok(efficiency[i], upper[i], lower[i])) {
          side = 0;
          count = 0;
          continue;
        }
        if (efficiency[i] >= rangeThreshold) {
          side = 0;
          count = 0;
          continue;
        }

        const lowerReached = touchByWick ? candles[i].low <= lower[i] : closes[i] <= lower[i];
        const upperReached = touchByWick ? candles[i].high >= upper[i] : closes[i] >= upper[i];
        const lowerTouch = lowerReached && (i === 0 || !(touchByWick ? candles[i - 1].low <= lower[i - 1] : closes[i - 1] <= lower[i - 1]));
        const upperTouch = upperReached && (i === 0 || !(touchByWick ? candles[i - 1].high >= upper[i - 1] : closes[i - 1] >= upper[i - 1]));

        // If a candle spans both edges, its intrabar direction is unknown; skip its signal.
        if (lowerTouch && upperTouch) {
          out[i] = side * count;
          continue;
        }
        if (side === 1 && upperTouch) {
          side = 0;
          count = 0;
        } else if (side === -1 && lowerTouch) {
          side = 0;
          count = 0;
        } else if (side === 0 && lowerTouch) {
          side = 1;
          count = 1;
        } else if (side === 0 && upperTouch) {
          side = -1;
          count = 1;
        } else if (side === 1 && lowerTouch) {
          count = Math.min(maxTrades, count + 1);
        } else if (side === -1 && upperTouch) {
          count = Math.min(maxTrades, count + 1);
        }
        out[i] = side * count;
      }
      return out;
    },
  },
  {
    id: "macd_cross",
    name: "MACD signal cross",
    blurb: "Long when the MACD line is above its signal line, short when below.",
    params: [
      { key: "fast", label: "Fast", value: 12, min: 2, max: 100 },
      { key: "slow", label: "Slow", value: 26, min: 3, max: 200 },
      { key: "signal", label: "Signal", value: 9, min: 2, max: 100 },
    ],
    positions(candles, p) {
      const m = macd(col(candles, "close"), p.fast, p.slow, p.signal);
      return candles.map((_, i) => (ok(m.line[i], m.signal[i]) ? (m.line[i] > m.signal[i] ? 1 : -1) : 0));
    },
  },
  {
    id: "donchian_breakout",
    name: "Donchian breakout",
    blurb: "Buy a new N-bar high, sell a new N-bar low, and stay in until the opposite break.",
    params: [{ key: "period", label: "Channel bars", value: 20, min: 2, max: 400 }],
    positions(candles, p) {
      const c = col(candles, "close");
      const d = donchian(col(candles, "high"), col(candles, "low"), p.period);
      return stateMachine(c.length, (i, pos) => {
        if (!ok(d.upper[i])) return pos;
        if (c[i] > d.upper[i]) return 1;
        if (c[i] < d.lower[i]) return -1;
        return pos;
      });
    },
  },
  {
    id: "supertrend_flip",
    name: "Supertrend flip",
    blurb: "Follow the Supertrend direction: long in an uptrend, short in a downtrend.",
    params: [
      { key: "period", label: "ATR period", value: 10, min: 2, max: 100 },
      { key: "mult", label: "Multiplier", value: 3, min: 0.5, max: 10, step: 0.1 },
    ],
    positions(candles, p) {
      const st = supertrend(col(candles, "high"), col(candles, "low"), col(candles, "close"), p.period, p.mult);
      return candles.map((_, i) => (ok(st.dir[i]) ? st.dir[i] : 0));
    },
  },
  {
    id: "stoch_reversion",
    name: "Stochastic reversion",
    blurb: "Buy when %K is oversold and crosses up through %D, sell when overbought and crossing down.",
    params: [
      { key: "k", label: "%K", value: 14, min: 2, max: 100 },
      { key: "d", label: "%D", value: 3, min: 1, max: 50 },
      { key: "low", label: "Oversold", value: 20, min: 1, max: 49 },
      { key: "high", label: "Overbought", value: 80, min: 51, max: 99 },
    ],
    positions(candles, p) {
      const s = stochastic(col(candles, "high"), col(candles, "low"), col(candles, "close"), p.k, p.d);
      return stateMachine(candles.length, (i, pos) => {
        if (i === 0 || !ok(s.k[i], s.d[i], s.k[i - 1], s.d[i - 1])) return pos;
        const up = s.k[i - 1] <= s.d[i - 1] && s.k[i] > s.d[i];
        const down = s.k[i - 1] >= s.d[i - 1] && s.k[i] < s.d[i];
        if (up && s.k[i] < p.low + 10) return 1;
        if (down && s.k[i] > p.high - 10) return -1;
        if (pos === 1 && s.k[i] > 50) return 0;
        if (pos === -1 && s.k[i] < 50) return 0;
        return pos;
      });
    },
  },
];

export function defaultParams(strategy) {
  const out = {};
  strategy.params.forEach((p) => {
    out[p.key] = p.value;
  });
  return out;
}
